import { PhotoIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui/atoms';

import { BANNER_PREVIEW } from '../../../utils/bannerMedia';

export interface BannerPreviewFrameProps {
  /** URL da arte (blob de arquivo novo ou caminho do servidor). */
  readonly imageUrl: string | undefined;
  /** URL do MP4 escolhido; quando presente, o frame renderiza vídeo. */
  readonly videoUrl: string | undefined;
  /** Título sobreposto; `undefined` quando "Exibir título" está desmarcado. */
  readonly overlayTitle: string | undefined;
  /**
   * Como o quadro exibe a mídia.
   *
   * - `card` — a caixa fixa 320 × 360 do dashboard, com recorte (`object-cover`).
   * - `modal` — a arte aparece inteira, com **no máximo 360px de altura**, e o quadro
   *   encolhe até ela: sem corte e sem faixa vazia.
   */
  readonly variant?: 'card' | 'modal';
  /** Reporta a resolução real da mídia depois que ela carrega. */
  readonly onMediaLoad?: (size: { width: number; height: number }) => void;
}

/**
 * Container do banner em duas exibições.
 *
 * No `card` reproduz o card do dashboard do app: caixa fixa de **320 × 360**
 * (`20rem × 22.5rem`), proporção **8:9** retrato, com a mídia **preenchendo** o
 * quadro (`object-cover`) — é o que o aluno vê, recorte incluído, e o motivo de o
 * conteúdo essencial da arte precisar ficar na zona central.
 *
 * No `modal` reproduz o "Novidades" do app: a arte ocupa **toda a largura** do
 * conteúdo com a **altura natural** do arquivo, inteira — é onde o operador enxerga o
 * que o recorte do card tira.
 */
export function BannerPreviewFrame({
  imageUrl,
  videoUrl,
  overlayTitle,
  variant = 'card',
  onMediaLoad,
}: BannerPreviewFrameProps) {
  const { t } = useTranslation('banner');
  const hasMedia = Boolean(videoUrl ?? imageUrl);
  const mediaUrl = videoUrl ?? imageUrl;

  // Arte de banner pesa: até carregar, o quadro ficaria com o fundo vazio e pareceria
  // preview sem mídia. Guardar **qual** URL carregou (em vez de um booleano com efeito
  // de reset) faz a troca de arquivo voltar ao skeleton sozinha: a URL muda, deixa de
  // bater com a carregada, e o elemento nem precisa ser desmontado.
  const [loadedMediaUrl, setLoadedMediaUrl] = useState<string | undefined>(undefined);
  const isMediaLoaded = mediaUrl !== undefined && loadedMediaUrl === mediaUrl;

  const isModal = variant === 'modal';
  // No card a mídia preenche a caixa fixa de 320 × 360 (recorte). No modal ela é o
  // próprio conteúdo do quadro: largura até a do modal, altura até 360px, proporção
  // preservada — sem corte e sem faixa vazia entre a borda e a arte. Arte deitada usa a
  // largura toda e fica mais baixa; arte em pé bate os 360px e fica mais estreita.
  const mediaClassName = isModal
    ? 'block max-w-full'
    : 'absolute inset-none h-full w-full object-cover';
  const mediaStyle = isModal ? { maxHeight: BANNER_PREVIEW.HEIGHT_REM } : undefined;

  return (
    <div
      className={`mx-auto overflow-hidden rounded-sm border-hairline border-ctx-layout-border ${
        isModal && isMediaLoaded ? 'w-fit' : 'w-full'
      }`}
      style={
        isModal
          ? undefined
          : {
              width: BANNER_PREVIEW.WIDTH_REM,
              height: BANNER_PREVIEW.HEIGHT_REM,
              maxWidth: '100%',
            }
      }
    >
      <div
        className={`relative flex w-full items-end justify-center ${
          isModal ? '' : 'h-full bg-base-gray-800'
        }`}
        // Sem mídia (ou antes de carregar) a altura do card serve de reserva: no modal
        // a arte é que dá altura, e o quadro colapsaria.
        style={isModal && !isMediaLoaded ? { minHeight: BANNER_PREVIEW.HEIGHT_REM } : undefined}
      >
        {/* No modal o quadro só encolhe até a mídia depois que ela carrega: antes
            disso (e sem mídia) a largura total é o que dá caixa ao skeleton e ao
            estado vazio. */}
        <div className={`relative flex items-end ${isModal ? 'w-fit' : 'h-full w-full'}`}>
          {videoUrl ? (
            // Toca sozinho, sem som, em loop e sem controles — a exibição ao aluno
            // não tem player, e o preview não deve inventar um.
            <video
              src={videoUrl}
              autoPlay
              muted
              loop
              playsInline
              className={mediaClassName}
              style={mediaStyle}
              aria-label={t('banner.form.preview.subtitle')}
              onLoadedMetadata={event => {
                setLoadedMediaUrl(mediaUrl);
                onMediaLoad?.({
                  width: event.currentTarget.videoWidth,
                  height: event.currentTarget.videoHeight,
                });
              }}
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={t('banner.form.preview.subtitle')}
              className={mediaClassName}
              style={mediaStyle}
              onLoad={event => {
                setLoadedMediaUrl(mediaUrl);
                onMediaLoad?.({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                });
              }}
            />
          ) : (
            <div className="absolute inset-none flex w-full flex-col items-center justify-center gap-nano px-mini text-center">
              <PhotoIcon className="h-xs w-xs text-base-gray-400" />
              <span className="ts-subtitle-xxs text-base-gray-200">
                {t('banner.form.preview.emptyTitle')}
              </span>
              <span className="ts-caption-xxs text-base-gray-400">
                {t('banner.form.preview.emptyHint')}
              </span>
            </div>
          )}

          {hasMedia && !isMediaLoaded ? (
            // Skeleton estático no lugar do quadro (sem pulsar: a mídia entra em cima
            // dele em instantes, e a animação piscava a cada troca de arquivo). O rótulo
            // por cima evita confundir com o estado sem mídia.
            <span className="absolute inset-none">
              <Skeleton className="h-full w-full animate-none rounded-none" />
              <span className="absolute inset-none flex items-center justify-center ts-caption-xxs text-ctx-content-base">
                {t('banner.form.preview.loading')}
              </span>
            </span>
          ) : null}

          {hasMedia && overlayTitle ? (
            <span className="absolute inset-x-none bottom-none bg-gradient-to-t from-base-gray-900 to-base-neutral-none px-micro pt-xs pb-micro ts-subtitle-xxs text-base-neutral-white">
              {overlayTitle}
            </span>
          ) : null}
        </div>

        {!hasMedia && overlayTitle ? (
          <span className="absolute inset-x-none bottom-none px-micro pb-micro ts-subtitle-xxs text-base-gray-200">
            {overlayTitle}
          </span>
        ) : null}
      </div>
    </div>
  );
}
